import { useState, forwardRef } from "react";
import Slider from "react-slick";
import styled from "styled-components";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { LinebreakParagraph, Paragraph } from "../Paragraph";
import { H2, H3 } from "../Heading";
import { Button } from "../Button";
import { Image } from "../Image";
import { Modal } from "../Modal";
import { IconButton } from "../IconButton";
import { IoMdClose } from "react-icons/io";
import bigBanner from "../../assets/banner.svg";
import newfeature from "../../assets/new-feature.svg";
import { Banner as BannerType } from "../../types/Banner";
import "./styles.css";

const BannerContainer = styled(Box)`
  ${({ theme: { fontSizes, colors, space, radii } }) => `
    max-width: 100%;
    width: 94% !important;
    overflow: hidden;
    background-color: ${colors.gray[30]};
    font-family: "Inter", sans-serif;
    font-size: ${fontSizes[0]};
    margin: ${space[4]};
    border-radius: ${radii[1]};
    padding: ${space[3]};
    height: 222px;
  `}
`;

const SmallBannerContainer = styled(Box)`
  ${({ theme: { fontSizes, colors, space, radii } }) => `
    max-width: 100%;
    width: 94% !important;
    overflow: hidden;
    background-color: ${colors.blue[100]};
    font-family: "Inter", sans-serif;
    font-size: ${fontSizes[0]};
    margin: ${space[4]};
    border-radius: ${radii[0]};
    padding: ${space[3]};
    color: ${colors.white};
  `}
`;

const NewFeature = styled(Flex)`
  ${({ theme: { fontSizes, colors, space, radii } }) => `
    border: 1px solid ${colors.gray[40]};
    gap: ${space[1]};
    font-size: ${fontSizes[1]};
    padding: ${space[1]};
    border-radius: ${radii[0]};
    align-items: center;
    font-weight: bold;
    color: ${colors.blue.primary};
    width: fit-content;
  `}
`;

interface BannerProps {
  banners: BannerType[];
  onDismiss: () => void;
  activeIndex: number;
  activeBannerRef: React.RefObject<HTMLDivElement>;
  setActiveIndex: any;
}

const BannerComponent = forwardRef<HTMLDivElement, BannerProps>(
  (
    { banners, onDismiss, activeIndex, activeBannerRef, setActiveIndex },
    ref
  ) => {
    const [isFindOutMoreOpen, setIsFindOutMoreOpen] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState<BannerType | null>(
      null
    );

    const settings = {
      dots: false,
      infinite: false,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: true,
      autoplay: true,
      autoplaySpeed: 5000, // Auto-rotation every 5 seconds
      pauseOnHover: true, // Pause auto-rotation on hover
      adaptiveHeight: true, // Adjusts height to content
      afterChange: (index: number) => setActiveIndex(index), // Update active index
    };

    const handleSeeMore = () => {
      if (selectedBanner) {
        window.open(selectedBanner.url, "_blank", "noreferrer");
      }
    };

    return (
      <>
        <Slider
          {...settings}
          style={{ maxWidth: "100%", overflow: "hidden" }}
          className={
            banners.some((b) => b.banner_type === "small")
              ? "small-banner-slider banner-slider"
              : "banner-slider"
          }
        >
          {banners.map((banner, index) => {
            const isActive = index === activeIndex; // Check if this banner is active

            const Container =
              banner.banner_type === "large"
                ? BannerContainer
                : SmallBannerContainer;

            return (
              <Container
                key={banner.id}
                ref={isActive ? activeBannerRef : null}
                className="banner-slide"
                id="bannerContainer"
              >
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  position="relative"
                  gap="50"
                >
                  {banner.banner_type === "large" ? (
                    <>
                      <IconButton
                        onClick={onDismiss}
                        Type={IoMdClose}
                        position="absolute"
                        right={"-14px"}
                        size="23px"
                        top={"-14px"}
                      />
                      <Box flex={1.5}>
                        <NewFeature>
                          <Image src={newfeature} alt="feature" />
                          New Notification
                        </NewFeature>
                        <H2 my={2}>{banner.title}</H2>
                        <LinebreakParagraph
                          minHeight={35}
                          height={68}
                          fontSize={1}
                          color={"gray.60"}
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 4, // Limits text to 4 lines
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {banner.short_description}
                        </LinebreakParagraph>
                        <Flex>
                          <Button
                            mr={2}
                            onClick={() => {
                              setIsFindOutMoreOpen(true);
                              setSelectedBanner(banner);
                            }}
                            width={"145px"}
                          >
                            Find out more
                          </Button>
                          <Button variant="blueText" onClick={onDismiss}>
                            Dismiss
                          </Button>
                        </Flex>
                      </Box>
                      <Box marginRight={"8%"} flex={0.5}>
                        <Box position={"absolute"} bottom={"-26px"}>
                          <Image
                            src={bigBanner}
                            alt="feature banner"
                            height="100%"
                            width="100%"
                          />
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Flex gap="20">
                        <Image width={"30px"} src={newfeature} alt="feature" />
                        <Box>
                          <H3 mb={"0px"} color={"white"}>
                            {banner.title}
                          </H3>
                          <Paragraph
                            my={0}
                            fontSize={1}
                            color={"white"}
                            fontWeight={100}
                            height={68}
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 4, // Limits text to 4 lines
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {banner.short_description}
                          </Paragraph>
                        </Box>
                      </Flex>
                      <Flex gap="20">
                        {/* <Button
                          variant="blueText"
                          color={"white"}
                          onClick={onDismiss}
                        >
                          Dismiss
                        </Button> */}

                        <IconButton
                          onClick={onDismiss}
                          Type={IoMdClose}
                          size="23px"
                        />
                        <Button
                          mr={2}
                          onClick={() => {
                            setIsFindOutMoreOpen(true);
                            setSelectedBanner(banner);
                          }}
                          minWidth={145}
                        >
                          Find out more
                        </Button>
                        <Box width={50}></Box>
                      </Flex>
                    </>
                  )}
                </Flex>
              </Container>
            );
          })}
        </Slider>

        {selectedBanner && (
          <Modal
            isOpen={isFindOutMoreOpen}
            onClose={() => setIsFindOutMoreOpen(false)}
            title={selectedBanner.title}
            content={selectedBanner.long_description}
            onModalConfirmClick={handleSeeMore}
            secondaryButtonText="Close"
            primaryButtonText="See More"
            primaryButtonVariant="primary"
            hasSecondaryButton
            width="500px"
            hasModalBannerImage={true}
          />
        )}
      </>
    );
  }
);

export default BannerComponent;
